import type { AppError, Result } from '../types';
/**
 * Represents a failed `Result` containing an error.
 *
 * @typeParam E - The type of the error (defaults to `unknown`).
 */
/**
 * @internal
 */
export class Err<E = AppError> {
  /**
   * Indicates that this is not an `Ok` result.
   */
  readonly isOk = false;

  /**
   * Indicates that this is an `Err` result.
   */
  readonly isErr = true;

  /**
   * The wrapped error value.
   *
   * @param error - The error to wrap.
   */
  constructor(public readonly error: E) {}

  /**
   * Maps the success value. Since this is `Err`, the success function is ignored and the same `Err` is returned.
   *
   * @typeParam U - The type of the mapped success value.
   * @param _fn - Ignored.
   * @returns The same `Err` instance.
   */
  map<U>(_fn: (val: never) => U): Result<U, E> {
    return new Err(this.error);
  }

  /**
   * Maps the error value by applying the given function.
   *
   * @typeParam U - The new error type.
   * @param fn - The function to apply to the error.
   * @returns A new `Err` containing the mapped error.
   */
  mapErr<U>(fn: (err: E) => U): Result<never, U> {
    return new Err(fn(this.error));
  }

  /**
   * Chains another operation that returns a `Result`.
   * Since this is `Err`, the function is ignored and the same `Err` is returned.
   *
   * @typeParam U - The type of the result from the chained operation.
   * @param _fn - Ignored.
   * @returns The same `Err` instance.
   */
  andThen<U>(_fn: (val: never) => Result<U, E>): Result<U, E> {
    return new Err(this.error);
  }

  /**
   * Gets the success value. Since this is `Err`, this throws an error.
   *
   * @throws Always throws because `Err` does not contain a success value.
   */
  getSuccess(): never {
    throw new Error('Tried to unwrap Ok value on an Err.');
  }

  /**
   * Gets a default value in case of an error.
   *
   * @typeParam T - The type of the default value.
   * @param defaultValue - The value to return.
   * @returns The provided default value.
   */
  getDefault<T>(defaultValue: T): T {
    return defaultValue;
  }

  /**
   * Gets the wrapped error value.
   *
   * @returns The error.
   */
  getFail(): E {
    return this.error;
  }

  /**
   * Pattern-matches on the `Result`.
   *
   * @typeParam U - The return type.
   * @param handlers - Object containing `ok` and `err` handlers.
   * @returns The result of the `err` handler.
   */
  match<U>(handlers: { ok: (value: never) => U; err: (error: E) => U }): U {
    return handlers.err(this.error);
  }
}
